import { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Alert, Button, Typography } from '@mui/material';
import { FallbackMap } from './FallbackMap.js';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  fallbackMode: boolean;
}

export class GisErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null, fallbackMode: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, fallbackMode: false };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('GIS Error Boundary caught error:', error, errorInfo);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, fallbackMode: false });
  };

  handleFallback = () => {
    this.setState({ fallbackMode: true });
  };

  render() {
    if (this.state.hasError) {
      if (this.state.fallbackMode) {
        return <FallbackMap />;
      }
      return (
        <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', p: 2, gap: 2 }}>
          <Alert severity="warning" sx={{ alignItems: 'center' }}>
            <Typography variant="subtitle2">GIS Viewer 초기화에 실패했습니다.</Typography>
          </Alert>
          <Typography variant="body2" color="text.secondary" sx={{ fontFamily: 'monospace' }}>
            {this.state.error?.message}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button variant="contained" size="small" onClick={this.handleRetry}>
              재시도
            </Button>
            <Button variant="outlined" size="small" onClick={this.handleFallback}>
              기본 지도 모드
            </Button>
          </Box>
        </Box>
      );
    }
    return this.props.children;
  }
}

export default GisErrorBoundary;
